interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<(canInstall: boolean) => void>();

const notify = () => {
  listeners.forEach((listener) => listener(deferredPrompt !== null));
};

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    notify();
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    notify();
  });
}

export const subscribeToPwaInstall = (listener: (canInstall: boolean) => void) => {
  listeners.add(listener);
  listener(deferredPrompt !== null);
  return () => {
    listeners.delete(listener);
  };
};

export const promptPwaInstall = async () => {
  if (!deferredPrompt) return 'unavailable' as const;

  await deferredPrompt.prompt();
  const { outcome } = await deferredPrompt.userChoice;
  deferredPrompt = null;
  notify();
  return outcome;
};

// iOS Safari never fires `beforeinstallprompt` — there is no programmatic
// install API, so it needs its own detection + manual instructions instead.
export const isIosDevice = (): boolean => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;

  const isIphoneOrIpad = /iPad|iPhone|iPod/.test(navigator.userAgent) && !('MSStream' in window);
  // iPadOS 13+ reports itself as "MacIntel" but still has touch support.
  const isIpadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;

  return isIphoneOrIpad || isIpadOS;
};

export const isRunningStandalone = (): boolean => {
  if (typeof window === 'undefined') return false;

  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
};
